/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import useResizeObserver from "@react-hook/resize-observer";
import { useCallback, useEffect, useRef, useState } from "react";
import GridLayout, { Layout } from "react-grid-layout";
import { useSelector } from "react-redux";
import * as portals from "react-reverse-portal";

import { Subscription } from "rxjs";
import { RootState } from "store/rootReducer";
import { type ISelectedWell, selectSelectedWells, useMapStore } from "stores/map";
import styled from "styled-components";
import { uid } from "utils";

import useFeatureGate from "hooks/useFeatureGate";
import { filterWidgetsByFeature } from "hooks/useFeatureGateConfig";

import { MapEntity } from "api/map";

import { GridWidget } from "models/dashboard";
import { Dashboard, DashboardWidget } from "models/workspace";

import { useFilterWatcher } from "components/filter";
import { useBaseLayerFilterSync } from "components/map/hooks/useBaseLayerFilterSync";
import { useBaseLayerRefactor } from "components/map/hooks/useBaseLayerRefactor";
import { applyMultiPhaseChartDefaults } from "components/multiphase-chart/util";
import { useMapEntitiesQuery } from "components/vis/hooks";
import { WellCardPanel } from "components/well-card-panel/WellCardPanel";
import { useWorkspaceContext } from "components/workspace/hooks/useWorkspaceContext";

import { EntityKind } from "../../models/entityKind";
import "./DashboardPane.scss";
import Widget from "./Widget";
import WidgetWrapper from "./WidgetWrapper";
import {
  ChartWidgetKey,
  MidstreamChartWidgetKey,
  RigFracChartWidgetKey,
  ThreeDWidgetKey,
  TypeWellDetailsWidgetKey,
  TypeWellEditorWidgetKey,
  WellStatusChartWidgetKey,
  XdaViewerWidgetKey,
  XdaWidgetKey
} from "./constants/widgets.constants";
import { useDashboardContext, useDashboardDispatch } from "./hooks";
import { useAutoEnableMidstream } from "./hooks/useAutoEnableMidstream";
import {
  calculateIdealWidgetSize,
  getWorkspaceDashboardCapabilities,
  setGridLayoutItemComponentState
} from "./utils";
import { applyNormalizationAndProductionSettingsChartBeta } from "./utils/applyNormalizationAndProductionSettings";
import { getSessionData } from "./utils/getSessionData";

let dashboardSub: Subscription = null;

const mapPortalNode = portals.createHtmlPortalNode();
const errorTitle = "We're sorry, it looks like we're having issues.";

/** react-grid-layout's own root class — see the `inert` effect below. */
const GRID_SELECTOR = ".react-grid-layout";

export interface DashboardPaneModel {
  className?: string;
  isPopoutDashboard?: boolean;
}

export default function DashboardPane({
  className = "",
  isPopoutDashboard = false
}: DashboardPaneModel) {
  // context and hooks

  // Incompatible with popout dashboards
  // See note in hook
  useFilterWatcher(isPopoutDashboard, EntityKind.Well);
  useFilterWatcher(isPopoutDashboard, EntityKind.Facility);
  const baseLayerRefactor = useBaseLayerRefactor();
  // Legacy base layer has no filter to post; popouts never show the map.
  // `=== true` keeps the flag's loading `undefined` from posting.
  useBaseLayerFilterSync(!isPopoutDashboard, baseLayerRefactor === true);

  const dashboardDispatch = useDashboardDispatch();
  const autoEnableMidstream = useAutoEnableMidstream();
  const { error: workspaceError, workspace, activeDashboardId } = useWorkspaceContext();
  const { hasFeature } = useFeatureGate();
  const {
    dashboard,
    isLoading: dashboardLoading,
    error,
    dashboardRefreshObserver,
    dashboardMode
  } = useDashboardContext();

  // state
  const [columns, setColumns] = useState(12);
  const [designMode, setDesignMode] = useState(false);
  const [containerWidth, setWidth] = useState(1200);
  const [, setHeight] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [node, setNode] = useState(null);
  const [refreshTxn, setRefreshTxn] = useState<string>(null);
  const [xdaResetTxn, setXdaResetTxn] = useState<string>(null);
  const [rowHeight, setRowHeight] = useState(30);
  const [showFullscreen, setShowFullscreen] = useState({
    mb: false
  });
  const [wells, setWells] = useState({});
  const [widgets, setWidgets] = useState<GridWidget[]>();

  // refs
  const containerRef = useRef(null);
  const margin = useRef<[number, number]>([0, 0]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const globalSettingsRefs = useRef<{ [key: string]: any }>({});
  // Prevents xda from remounting
  const xdaPortalNodesRef = useRef<{ [widgetId: string]: portals.HtmlPortalNode }>({});

  // state from store
  const selectedWells = useMapStore(selectSelectedWells);
  const { mapEntities } = useMapEntitiesQuery();
  const useGlobalNormalizeBy = useSelector(
    (state: RootState) => state.normalizeBy.useNormalizeBy
  );
  const globalNormalizeBy = useSelector(
    (state: RootState) => state.normalizeBy.globalNormalizeBy
  );
  const dataSourceSetting = useSelector(
    (state: RootState) => state.app.pdenDataSourceSetting
  );

  // Update the design mode.
  useEffect(() => {
    setDesignMode(dashboardMode === "edit");
  }, [dashboardMode]);

  // Derive well colours/groups from the shared mapEntities query.
  // useMapEntitiesQuery is also consumed by XdaViewer and ThreeDViewer; React
  // Query deduplicates concurrent requests for the same key so only one network
  // call is made regardless of how many consumers are mounted.
  useEffect(() => {
    if (!mapEntities) {
      return;
    }

    const entityData = mapEntities.data;
    const temp: { [uwi: string]: ISelectedWell } = {};

    if (entityData && entityData.length > 0) {
      const entitiesDict: { [name: string]: MapEntity } = {};
      for (const ent of entityData) {
        entitiesDict[ent.uwi] = ent;
      }
      for (const selected of Object.keys(selectedWells)) {
        if (entitiesDict[selected]) {
          temp[selected] = {
            color: entitiesDict[selected].color,
            Uwi: selectedWells[selected].Uwi,
            group: entitiesDict[selected].group
          };
        }
      }
    }

    setWells(mapEntities.status === 200 ? temp : selectedWells);
  }, [mapEntities, selectedWells]);

  const getRowHeight = (height: number) => {
    return Math.max(Math.trunc(height / 12.0), 30);
  };

  useResizeObserver(containerRef, (entry) => {
    setWidth(entry.contentRect.width);
    setHeight(entry.contentRect.height);
    setRowHeight(getRowHeight(entry.contentRect.height));
  });

  /**
   * The one condition behind both the fullscreen sheet and the `inert`
   * marking on the grid it covers. Hoisted so the two cannot drift: a sheet
   * with no inert grid under it is the bug this whole mechanism exists to
   * fix, and an inert grid with no sheet over it is a dead dashboard.
   */
  const isFullscreenOverlayOpen = isFullscreen && dashboardMode === "explore";

  /**
   * The fullscreen overlay is an opaque sheet drawn OVER a grid that stays
   * mounted (see the `.fullscreen` block below and in DashboardPane.scss), so
   * every covered widget keeps a live rect and a live position. Without a
   * signal saying otherwise, anything that reasons about "is this on screen"
   * from geometry alone gets the wrong answer: keyboard focus tabs into
   * widgets nobody can see, screen readers read them out, and the guided
   * walkthrough spotlights them under the sheet.
   *
   * `inert` is the one attribute that says "painted, but not reachable" to all
   * three at once. It is driven by the same `isFullscreenOverlayOpen` the
   * `.fullscreen` block renders on, rather than by a second copy of the
   * condition: the two must agree, and leaving the grid inert with no overlay
   * over it makes the whole dashboard unusable.
   *
   * ponytail: set by query rather than a ref, because wrapping GridLayout in a
   * ref-bearing div would reparent `.react-grid-layout`'s `min-height: 100%`
   * onto an auto-height parent and collapse the grid.
   */
  useEffect(() => {
    const grid = containerRef.current?.querySelector(GRID_SELECTOR);
    if (!grid) {
      // `.react-grid-layout` is react-grid-layout's own class, so an upgrade
      // that renames it would stop the marking silently — and the failure mode
      // is invisible: focus, screen readers and the walkthrough all go back to
      // treating covered widgets as reachable, with nothing to catch it.
      if (process.env.NODE_ENV !== "production" && isFullscreenOverlayOpen) {
        // eslint-disable-next-line no-console
        console.warn(
          `[dashboard] no "${GRID_SELECTOR}" under the pane, so the covered grid ` +
            `cannot be marked inert. If react-grid-layout renamed its root class, ` +
            `update GRID_SELECTOR — keyboard focus, screen readers and the guided ` +
            `walkthrough all depend on this.`
        );
      }
      return;
    }

    if (isFullscreenOverlayOpen) {
      grid.setAttribute("inert", "");
    } else {
      grid.removeAttribute("inert");
    }
  }, [isFullscreenOverlayOpen, widgets]);

  // Reset the component back to initial state.
  useEffect(() => {
    if (dashboardRefreshObserver && !dashboardSub) {
      dashboardSub = dashboardRefreshObserver.subscribe((payload: string) => {
        setRefreshTxn(payload);
        // Only trigger XDA XdaSettingsStoreProvider remount for manual refresh
        if (payload.startsWith("xda-reset:")) {
          setXdaResetTxn(payload);
        }
      });
    }
    return () => {
      if (dashboardSub) {
        dashboardSub.unsubscribe();
        dashboardSub = null;
      }
    };
  }, [dashboardRefreshObserver]);

  // Auto-enable midstream based on dashboard capabilities
  useEffect(() => {
    if (workspace?.title === "Midstream") {
      const capabilities = null;
      autoEnableMidstream.update(capabilities, workspace.title);
      return;
    }

    if (!workspace?.dashboards?.length || !activeDashboardId) return;

    const activeDashboard = workspace.dashboards.find(
      (d) => d.dashboardId === activeDashboardId
    );

    const capabilities = getWorkspaceDashboardCapabilities(activeDashboard, hasFeature);
    autoEnableMidstream.update(capabilities, workspace.title);
  }, [workspace?.dashboards, activeDashboardId, workspace?.title]);

  // Update the global settings references when the global settings change for the current dashboard only
  useEffect(() => {
    if (dashboard) {
      dashboard.widgets.forEach((item: DashboardWidget) => {
        const key = `${item.type}::${item.widgetId}`;

        globalSettingsRefs.current[key] = {
          useNormalizeBy: useGlobalNormalizeBy,
          normalizeBy: globalNormalizeBy,
          timeStep: dataSourceSetting?.timeStep,
          source: dataSourceSetting?.source
        };
      });
    }
  }, [globalNormalizeBy, useGlobalNormalizeBy, dataSourceSetting]);

  // Build the widgets state when the dashboard changes or there is a new dashboard refresh transaction.
  useEffect(() => {
    if (dashboard) {
      const activeDashboard: Dashboard = {
        ...dashboard,
        widgets: filterWidgetsByFeature(hasFeature, dashboard.widgets ?? [])
      };

      const gridLayoutItems = activeDashboard.widgets.map(
        (item: DashboardWidget, index: number): GridWidget => {
          const gridLayoutItem: GridWidget = {
            x: item.x,
            y: item.y,
            w: item.width,
            h: item.height,
            i: item.widgetId,
            name: item.type,
            static: false,
            ref: item.widgetId,
            componentIndex: index,
            component: item.type,
            componentState: undefined,
            componentOptions: undefined
          };

          const currentGlobalSettings = {
            useNormalizeBy: useGlobalNormalizeBy,
            normalizeBy: globalNormalizeBy,
            timeStep: dataSourceSetting?.timeStep,
            source: dataSourceSetting?.source
          };

          const key = `${item.type}::${item.widgetId}`;
          const previousGlobalSettings = globalSettingsRefs.current[key] || {};
          // Previous global settings are uninitialized on hard refresh
          const isHardRefresh = Object.keys(previousGlobalSettings).length === 0;

          //load from session before defaults
          const sessionData = getSessionData(item);

          if (sessionData) {
            // guard to protect against stale session data that could still be the old chart settings
            // if it is old, it will translate, else it will set the component state from session data
            setGridLayoutItemComponentState(sessionData, item, gridLayoutItem);
            if (item.type === ChartWidgetKey) {
              // TODO: rename this function when we are confident in not rolling back to old chart
              applyNormalizationAndProductionSettingsChartBeta(
                gridLayoutItem,
                item,
                isHardRefresh,
                previousGlobalSettings,
                currentGlobalSettings
              );
            }
          } else if (item.settings) {
            setGridLayoutItemComponentState(item.settings, item, gridLayoutItem);
          }

          // Update reference to global settings after session storage is loaded
          globalSettingsRefs.current[key] = currentGlobalSettings;

          switch (item.type) {
            case "map": {
              // Ensure the map widget has specific ID, ref and portal to ensure consistency
              // when switching dashboards.
              gridLayoutItem.i = "mb";
              gridLayoutItem.ref = "mb";
              gridLayoutItem.componentOptions = {
                portalNode: mapPortalNode
              };
              break;
            }
            case MidstreamChartWidgetKey:
            case RigFracChartWidgetKey:
            case WellStatusChartWidgetKey:
            case ChartWidgetKey:
              gridLayoutItem.componentOptions = {
                portalNode: portals.createHtmlPortalNode()
              };
              // gridLayoutItem.componentState.id = item.widgetId; // TODO: we dont have an id on our new state - do we need one?
              break;
            case "multiphase-chart": {
              gridLayoutItem.componentOptions = {
                portalNode: portals.createHtmlPortalNode()
              };
              gridLayoutItem.componentState = applyMultiPhaseChartDefaults(
                gridLayoutItem.componentState
              );
              gridLayoutItem.componentState.id = item.widgetId;
              break;
            }
            case "feature-importance": {
              gridLayoutItem.componentOptions = {
                portalNode: portals.createHtmlPortalNode()
              };
              break;
            }
            case "completion":
            case "survey-vis":
            case "threed":
            case "xdaviewer":
            case "xda": {
              if (!xdaPortalNodesRef.current[item.widgetId]) {
                xdaPortalNodesRef.current[item.widgetId] = portals.createHtmlPortalNode();
              }
              gridLayoutItem.componentOptions = {
                portalNode: xdaPortalNodesRef.current[item.widgetId]
              };
              break;
            }
            case TypeWellEditorWidgetKey: {
              gridLayoutItem.componentOptions = {
                portalNode: portals.createHtmlPortalNode()
              };
              break;
            }
            case TypeWellDetailsWidgetKey: {
              gridLayoutItem.componentOptions = {
                portalNode: portals.createHtmlPortalNode()
              };
              break;
            }
            default: {
              break;
            }
          }
          return gridLayoutItem;
        }
      );

      // gridLayoutItems should contains all the widgets that we have on screen
      if (!isPopoutDashboard && !gridLayoutItems.find((x) => x.i === "mb")) {
        // Add a hidden map
        gridLayoutItems.push({
          x: 0,
          y: 0,
          w: 0,
          h: 0,
          minW: 0,
          minH: 0,
          i: "mb",
          name: "map",
          static: false,
          ref: "mb",
          componentIndex: gridLayoutItems.length,
          component: "map",
          componentState: undefined,
          componentOptions: {
            portalNode: mapPortalNode
          }
        });
      }

      setWidgets(gridLayoutItems);
      if (activeDashboard.layout.width) {
        setColumns(activeDashboard.layout.width);
      }

      // Reset the full screen
      setShowFullscreen({
        mb: false
      });
      setIsFullscreen(false);
      setNode(null);
    }
  }, [dashboard, refreshTxn, designMode]);

  // Take action when a widget is removed from the dashboard.
  const onRemoveWidget = useCallback(
    (widget: GridWidget) => {
      if (!dashboard?.widgets) return;

      const modifiedWidgets: DashboardWidget[] = [];
      dashboard.widgets.forEach((x) => {
        const copy = { ...x };
        // The map uses a special 'i' value, so it needs to be treated special.
        if (widget.component === "map") {
          if (x.type !== "map") {
            modifiedWidgets.push(copy);
          }
        } else if (copy.widgetId !== widget.i) {
          modifiedWidgets.push(copy);
        }
      });

      const modifiedDashboard: Dashboard = {
        ...dashboard,
        widgets: modifiedWidgets
      };

      dashboardDispatch({
        payload: {
          isModified: true,
          dashboard: modifiedDashboard
        }
      });
    },
    [dashboard, dashboardDispatch]
  );

  // Take action when the layout changes, usually due to a widget moving or being resized.
  const onLayoutChanged = useCallback(
    (layout: Layout[]) => {
      if (!dashboard?.widgets || dashboardMode !== "edit") return;

      // Ignore if the widget length does not match as this is will handled by onDrop.
      if (dashboard.widgets.length != layout.filter((l) => l.w > 0 && l.h > 0).length)
        return;

      const modifiedWidgets: DashboardWidget[] = [];
      dashboard.widgets.forEach((x) => {
        const match = layout.find(
          (l) =>
            l.i === x.widgetId || (x.type === "map" && l.i === "mb" && l.w > 0 && l.h > 0)
        );
        if (match) {
          const copy = { ...x };
          copy.x = match.x;
          copy.y = match.y;
          copy.width = match.w;
          copy.height = match.h;
          modifiedWidgets.push(copy);
        }
      });

      const modifiedDashboard: Dashboard = {
        ...dashboard,
        widgets: modifiedWidgets
      };

      dashboardDispatch({
        payload: {
          isModified: true,
          dashboard: modifiedDashboard
        }
      });
    },
    [dashboard, dashboardDispatch, dashboardMode]
  );

  // Take action when a new widget is dropped onto the dashboard
  const onDrop = useCallback(
    (layout: Layout[], layoutItem: Layout, e) => {
      if (!layoutItem || !dashboard?.widgets) return;

      const data = JSON.parse(e.dataTransfer.getData("dragData"));
      const modifiedWidgets: DashboardWidget[] = [];
      dashboard.widgets.forEach((x) => {
        const match = layout.find(
          (l) => l.i === x.widgetId || (x.type === "map" && l.i === "mb")
        );
        if (match) {
          const copy = { ...x };
          copy.x = match.x;
          copy.y = match.y;
          modifiedWidgets.push(copy);
        }
      });
      const id = uid();

      // Calculate the ideal size of the dropped widget based on the current layout.
      const { x, y, w, h } = calculateIdealWidgetSize(
        data.component,
        layoutItem,
        layout,
        dashboard?.layout
      );

      const newWidget: DashboardWidget = {
        widgetId: id,
        type: data.component,
        title: data.component,
        x: x,
        y: y,
        width: w,
        height: h,
        settings: JSON.stringify(data.componentState)
      };

      modifiedWidgets.push(newWidget);

      const modifiedDashboard: Dashboard = {
        ...dashboard,
        widgets: modifiedWidgets
      };

      dashboardDispatch({
        payload: {
          isModified: true,
          dashboard: modifiedDashboard
        }
      });
    },
    [dashboard, dashboardDispatch]
  );

  const setFullscreen = (item) => (isFullscreen) => {
    const copy = { ...showFullscreen };
    copy[item.ref] = isFullscreen;
    setShowFullscreen(copy);
    setNode(item.componentOptions.portalNode);
    setIsFullscreen(isFullscreen);
  };

  // Handle an error loading the dashboard.
  if ((workspaceError || error) && !dashboardLoading) {
    return (
      <DashboardErrorContainer
        className={`ComponentPane ${className}`}
        ref={containerRef}>
        <h5>{errorTitle}</h5>
      </DashboardErrorContainer>
    );
  }

  return (
    <div className={`ComponentPane ${className}`} ref={containerRef}>
      {widgets && (
        <GridLayout
          layout={widgets}
          className="layout"
          isDraggable={designMode}
          draggableHandle=".widget-title"
          isResizable={designMode}
          resizeHandles={designMode ? ["se"] : []}
          onLayoutChange={(layout: Layout[]) => onLayoutChanged(layout)}
          isDroppable={designMode}
          onDrop={(layout: Layout[], layoutItem: Layout, e) =>
            onDrop(layout, layoutItem, e)
          }
          compactType="vertical"
          onDropDragOver={() => {
            return { w: 2, h: 2 };
          }}
          cols={columns}
          margin={margin.current}
          width={containerWidth}
          rowHeight={rowHeight}>
          {widgets.map((item: GridWidget) => {
            const isXdaWidget =
              item.component === XdaWidgetKey ||
              item.component === XdaViewerWidgetKey ||
              item.component === ThreeDWidgetKey;
            return (
              <div key={item.i}>
                <WidgetWrapper
                  widget={item}
                  designMode={designMode}
                  onRemoveWidget={(widget: GridWidget) => onRemoveWidget(widget)}>
                  <Widget
                    widget={item}
                    showFullscreen={showFullscreen[item.ref]}
                    onFullscreen={setFullscreen(item)}
                    selectedWells={selectedWells}
                    wells={wells}
                    refreshKey={isXdaWidget && xdaResetTxn ? xdaResetTxn : undefined}
                  />
                </WidgetWrapper>
              </div>
            );
          })}
        </GridLayout>
      )}

      {isFullscreenOverlayOpen && (
        <div className="fullscreen">
          <portals.OutPortal node={node}></portals.OutPortal>
        </div>
      )}

      {/* Mounted on every dashboard except a popped-out one, but reachable only
          from a map: the single way in is View Well Card in the map's click
          popup. Well-list and data-table row clicks only switch the well of a
          card that is already open. So on a dashboard with no map widget the
          card is mounted and unreachable. That is accepted for v1 — giving the
          row lists their own opener is an entry-point decision, not a fix.

          A popped-out window is excluded because the card belongs to the main
          window, not because it has no map: it renders whatever widgets the
          dashboard was saved with, a map among them. `Map.tsx` therefore gates
          the popup on the same flag, or the popout would offer View Well Card
          with nothing to open. */}
      {!isPopoutDashboard && dashboard?.dashboardId && (
        <WellCardPanel dashboardId={dashboard.dashboardId} />
      )}
    </div>
  );
}

const DashboardErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
`;
